import axios from 'axios';
import * as R from 'ramda';
import message from 'antd/lib/message';

import { normalize } from 'normalizr';
import { batchActions } from 'redux-batched-actions';

import * as ApikoApi from '../../api/apiko-api';
import * as schemes from '../../api/schemes';

import * as actions from '../actions/contacts';
import { fetchTasksSuccess } from '../actions/tasks';
import { fetchCommentsSuccess } from '../actions/comments';
import { updateCompanyContacts } from '../actions/companies';
import { getSpecificRequestsWithContacts } from '../selectors';
import { fetchRequestLogsSuccess } from '../actions/request-logs';
import { fetchMeetingNotesSuccess } from '../actions/meeting-notes';
import { fetchRequestsSuccess, deleteRequestSuccess } from '../actions/requests';

import { getResErrorMsg } from '../../utils';

const getCurrentPage = ({ contacts: { page } }) => page;
const getNextPage = ({ contacts: { page } }) => page + 1;

const getContactsFilters = ({
  contacts: {
    filters: {
      type, countries,
    },
  },
}) => ({
  type,
  countries: countries[0] && countries.join(),
});

const commonActions = entities => [
  fetchRequestsSuccess({
    requests: entities.requests,
    notNewPage: true,
  }),
  fetchMeetingNotesSuccess({
    meetingNotes: entities.meetingNotes,
    notNewPage: true,
  }),
  fetchCommentsSuccess(entities.comments),
  fetchTasksSuccess({
    tasks: entities.tasks,
    notNewPage: true,
  }),
  fetchRequestLogsSuccess({
    logs: entities.logs,
  }),
];


/**
 * fetchContact :: String -> Func -> Promise Error _
 */
export const fetchContact = contactId => dispatch =>
  Promise.resolve(ApikoApi.fetchContact(contactId))
    .then(contact => dispatch(actions.fetchContactSuccess(contact)));


/**
 * fetchContacts :: (Object, Bool) -> (Func, Func) -> Promise Error _
 */
export const fetchContacts = (cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(!polling && dispatch(actions.fetchContactsRequest()))
    .then(() => ApikoApi.fetchContacts({
      page: polling ? getCurrentPage(getState()) : getNextPage(getState()),
      ...getContactsFilters(getState()),
      cancelToken,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.contacts,
      );

      dispatch(batchActions([
        actions.fetchContactsSuccess({
          polling,
          count: result.count,
          ids: result.contacts,
          contacts: entities.contacts,
          existsMore: result.existsMore,
          ...(polling ? { notNewPage: true } : {}),
        }),
        ...commonActions(entities),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.fetchContactsFailure());
    });


/**
 * searchContacts :: (String, Object, Boolean) -> (Func, Func) -> Promise Error
 */
export const searchContacts = (searchTerm, cancelToken, polling = false) => (dispatch, getState) =>
  Promise.resolve(!polling && dispatch(actions.searchContactsRequest()))
    .then(() => ApikoApi.fetchContacts({
      ...getContactsFilters(getState()),
      page: polling ? getCurrentPage(getState()) : getNextPage(getState()),
      cancelToken,
      searchTerm,
      polling,
    }))
    .then(({ data, responseTime }) => {
      const { result, entities } = normalize(
        data,
        schemes.contacts,
      );

      dispatch(batchActions([
        actions.searchContactsSuccess({
          polling,
          count: result.count,
          ids: result.contacts,
          contacts: entities.contacts,
          existsMore: result.existsMore,
          ...(polling ? { notNewPage: true } : {}),
        }),
        ...commonActions(entities),
      ]));

      return responseTime;
    })
    .catch((e) => {
      if (axios.isCancel(e)) return;

      if (!polling) dispatch(actions.searchContactsFailure());
    });


/**
 * fetchContactThreads :: (Number, String, Object) -> Func -> Promise Error
 */
export const fetchContactThreads = (page, contactId, cancelToken) => dispatch =>
  Promise.resolve(dispatch(actions.fetchContactThreadsRequest()))
    .then(() => ApikoApi.fetchThreadsByContact(page, contactId, cancelToken))
    .then(({ threads, count }) =>
      dispatch(actions.fetchContactThreadsSuccess({
        page,
        threads,
        count,
        contactId,
      })))
    .catch((e) => {
      console.log(e);
      dispatch(actions.fetchContactThreadsFailure());
    });


/**
 * fetchContactThreadMessages :: (Number, String, Object) -> Func -> Promise Error
 */
export const fetchContactThreadMessages = (page, threadId, contactId, cancelToken) => dispatch =>
  Promise.resolve(dispatch(actions.fetchContactThreadMessagesRequest()))
    .then(() => ApikoApi.fetchThreadMessages({ page, threadId, cancelToken }))
    .then(({ thread }) =>
      dispatch(actions.fetchContactThreadMessagesSuccess({
        thread,
        page,
        contactId,
      })))
    .catch((e) => {
      console.log(e);
      dispatch(actions.fetchContactThreadMessagesFailure());
    });


/**
 * handleContactCreate :: Object -> Func -> Promise Error _
 */
export const handleContactCreate = data => dispatch =>
  Promise.resolve(dispatch(actions.createContactRequest()))
    .then(() => ApikoApi.createContact(data))
    .then(res => dispatch(actions.createContactSuccess(res)));


/**
 * handleContactUpdate :: (ID, Object) -> (Func, Func) -> Promise Error _
 */
export const handleContactUpdate = (contactId, data) => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.updateContactRequest()))
    .then(() => ApikoApi.updateContactById(contactId, data))
    .then((updatedContact) => {
      const contact = R.path(['contacts', 'items', contactId], getState());

      const updatedContactWithCompany = updatedContact.company
        ? updatedContact
        : { ...updatedContact, company: {} };

      const requests = getSpecificRequestsWithContacts(getState(), { requests: contact.requests });

      return dispatch(batchActions([
        actions.updateContactSuccess(updatedContactWithCompany),
        ...(contact.companyId !== updatedContact.companyId
          ? [updateCompanyContacts({ contact: { ...contact, requests }, type: 'contact-update' })]
          : []),
      ]));
    });


/**
 * handleContactDelete :: ID -> (Func, Func) -> Promise Error _
 */
export const handleContactDelete = contactId => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.deleteContactRequest()))
    .then(() => ApikoApi.deleteContactById(contactId))
    .then(() => {
      const contact = R.path(['contacts', 'items', contactId], getState());

      return dispatch(batchActions([
        actions.deleteContactSuccess(contactId),
        ...(contact.companyId
          ? [updateCompanyContacts({
            type: 'contact-delete',
            contact: { ...contact, requests: [] },
          })]
          : []),
      ]));
    })
    .then(() => message.success('Contact successfully removed'))
    .catch((err) => {
      message.error(`${getResErrorMsg(err)}`);

      return dispatch(actions.deleteContactFailure());
    });


/**
 * handleContactWithRequestsDelete :: ID -> (Func, Func) -> Promise Error _
 */
export const handleContactWithRequestsDelete = contactId => (dispatch, getState) =>
  Promise.resolve(dispatch(actions.deleteContactRequest()))
    .then(() => ApikoApi.deleteContactWithRequestsById(contactId))
    .then(({ data: { requestIds } }) => {
      const contact = R.path(['contacts', 'items', contactId], getState());

      return dispatch(batchActions([
        deleteRequestSuccess(requestIds),
        actions.deleteContactSuccess(contactId),
        ...(contact.companyId
          ? [updateCompanyContacts({
            type: 'contact-delete',
            contact: { ...contact, requests: requestIds },
          })]
          : []),
      ]));
    })
    .then(() => message.success('Contact with Requests successfully removed.'))
    .catch((err) => {
      message.error(`${getResErrorMsg(err)}`);

      return dispatch(actions.deleteContactFailure());
    });


/**
 * deleteContactThread :: (String, String) -> Func -> Promise
 */
export const deleteContactThread = (contactId, threadId) => dispatch =>
  Promise.resolve(dispatch(actions.deleteContactThreadSuccess({ contactId, threadId })))
    .then(() => ApikoApi.deleteContactThread(threadId))
    .catch(e => console.log(e));



// WEBPACK FOOTER //
// ./src/redux/thunk/contacts.js